import React from 'react';
import { Brain, Cloud, Gamepad2, Users, Palette } from 'lucide-react';

interface Skill {
  name: string;
  level: number;
  category: string;
}

const SkillsSection: React.FC = () => {
  const skills: Skill[] = [
    // AI & Machine Learning
    { name: 'Python', level: 95, category: 'ai-ml' },
    { name: 'TensorFlow', level: 90, category: 'ai-ml' },
    { name: 'PyTorch', level: 85, category: 'ai-ml' },
    { name: 'OpenAI Gym', level: 80, category: 'ai-ml' },
    { name: 'Reinforcement Learning', level: 90, category: 'ai-ml' },
    { name: 'Large Language Models', level: 85, category: 'ai-ml' },
    { name: 'ChatGPT/Gemini', level: 90, category: 'ai-ml' },
    { name: 'NumPy/Pandas', level: 90, category: 'ai-ml' },
    
    // Cloud & Backend
    { name: 'AWS', level: 85, category: 'cloud-backend' },
    { name: 'Google Cloud Platform', level: 80, category: 'cloud-backend' },
    { name: 'Node.js', level: 85, category: 'cloud-backend' },
    { name: 'JavaScript/TypeScript', level: 90, category: 'cloud-backend' },
    { name: 'React', level: 85, category: 'cloud-backend' },
    { name: 'MySQL', level: 80, category: 'cloud-backend' },
    
    // Game Development & VR
    { name: 'Unity', level: 90, category: 'game-vr' },
    { name: 'C#', level: 85, category: 'game-vr' },
    { name: 'WebGL', level: 80, category: 'game-vr' },
    { name: 'VR/XR Development', level: 85, category: 'game-vr' },
    { name: 'OpenGL', level: 75, category: 'game-vr' },
    
    // Product & Project Management
    { name: 'Agile/Scrum', level: 90, category: 'product-management' },
    { name: 'Team Leadership', level: 85, category: 'product-management' },
    { name: 'Stakeholder Management', level: 90, category: 'product-management' },
    { name: 'R&D Strategy', level: 85, category: 'product-management' },
    { name: 'Public Speaking', level: 80, category: 'product-management' },
    
    // Development Tools
    { name: 'Git/GitHub', level: 90, category: 'tools' },
    { name: 'Jira/ClickUp', level: 85, category: 'tools' },
    { name: 'Figma', level: 80, category: 'tools' },
    { name: 'VSCode', level: 90, category: 'tools' },
    { name: 'MATLAB', level: 75, category: 'tools' },
  ];

  const categories = [
    { id: 'ai-ml', name: 'AI & Machine Learning', icon: Brain, color: 'text-purple-600' },
    { id: 'cloud-backend', name: 'Cloud & Backend', icon: Cloud, color: 'text-blue-600' },
    { id: 'game-vr', name: 'Game Dev & VR', icon: Gamepad2, color: 'text-green-600' },
    { id: 'product-management', name: 'Product Management', icon: Users, color: 'text-orange-600' },
    { id: 'tools', name: 'Development Tools', icon: Palette, color: 'text-red-600' },
  ];

  const getSkillsByCategory = (category: string) => {
    return skills.filter(skill => skill.category === category);
  };

  return (
    <section className="section-padding">
      <div className="container-max">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
            Skills & Technologies
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Experienced Product Owner with 6+ years of expertise in leading AI and ML projects. 
            Specialized in design of human-AI systems integrating large language models and reinforcement learning.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 bg-white/40 rounded-2xl p-8">
          {categories.map((category) => (
            <div key={category.id} className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg bg-${category.color.split('-')[1]}-100`}>
                  <category.icon className={`h-6 w-6 ${category.color}`} />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900">
                  {category.name}
                </h3>
              </div>
              
              <div className="space-y-4">
                {getSkillsByCategory(category.id).map((skill) => (
                  <div key={skill.name}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-secondary-700">
                        {skill.name}
                      </span>
                      <span className="text-sm text-secondary-500">
                        {skill.level}%
                      </span>
                    </div>
                    <div className="w-full bg-secondary-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Skills */}
        <div className="mt-16 text-center bg-white/40 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-secondary-900 mb-8">
            Additional Expertise
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'C/C++', 'Java', 'HTML/CSS', 'AutoCAD', 'Civil 3D', 'Adobe Photoshop', 
              'Adobe Illustrator', 'Microsoft Office Suite', 'IBM Watson', 'Stable Baselines',
              'scikit-learn', 'Keras', 'WebXR', 'Human-Computer Interaction', 'Research & Publications'
            ].map((skill) => (
              <span
                key={skill}
                className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection; 